/**
 * @file ssoService.js
 * @description Service layer for handling operations related to SSO.
 */
import { systemHttp, clientApiHttp, http, userApiHttp } from './httpService';

const getTenantId = () => +window.localStorage.getItem('tenantId');

export const fetchSystemSSOList = async () => {
    try {
        const response = await systemHttp.get('/v2/system/sso/list');
        return response.data;
    } catch (error) {
        console.error('Error fetching system SSO list:', error);
        throw error;
    }
};

export const fetchSystemSSOById = async id => {
    try {
        const response = await systemHttp.get(`/v2/system/sso/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching system SSO with ID ${id}:`, error);
        throw error;
    }
};

export const fetchSystemSSOClients = async id => {
    try {
        const response = await systemHttp.get(`/v2/system/sso/${id}/clients`);
        return response.data;
    } catch (error) {
        console.error(
            `Error fetching clients for system SSO with ID ${id}:`,
            error,
        );
        throw error;
    }
};

export const deleteSystemSSO = async id => {
    try {
        const response = await systemHttp.delete(`/v2/system/sso/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error deleting system SSO with ID ${id}:`, error);
        throw error;
    }
};

export const createSystemSSO = async ssoData => {
    try {
        const response = await systemHttp.post('/v2/system/sso', ssoData);
        return response.data;
    } catch (error) {
        console.error('Error creating system SSO:', error);
        throw error;
    }
};

export const updateSystemSSO = async ssoData => {
    try {
        const response = await systemHttp.put('/v2/system/sso', ssoData);
        return response.data;
    } catch (error) {
        console.error('Error updating system SSO:', error);
        throw error;
    }
};

// Client SSO operations
export const fetchClientSSOList = async () => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/sso/list`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching client SSO list:', error);
        throw error;
    }
};

export const fetchClientSSOById = async id => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/sso/${id}`,
        );
        return response.data;
    } catch (error) {
        console.error(`Error fetching client SSO with ID ${id}:`, error);
        throw error;
    }
};

export const deleteClientSSO = async id => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/sso/${id}`,
        );
        return response.data;
    } catch (error) {
        console.error(`Error deleting client SSO with ID ${id}:`, error);
        throw error;
    }
};

export const createClientSSO = async ssoData => {
    try {
        const tenantId = getTenantId();
        const payload = {
            ...ssoData,
            tenantId: tenantId,
        };
        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/sso`,
            payload,
        );
        return response.data;
    } catch (error) {
        console.error('Error creating client SSO:', error);
        throw error;
    }
};

export const updateClientSSO = async ssoData => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/sso`,
            ssoData,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating client SSO:', error);
        throw error;
    }
};

export const fetchClientContacts = async () => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/contact`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching client contacts:', error);
        throw error;
    }
};

// Legacy Client SSO operations
export const loginClientSSO = async ssoData => {
    try {
        const response = await userApiHttp.get(
            `/v2/user/sso/${ssoData.ssoId}/login?redirectUrl=${encodeURIComponent(ssoData.url)}`,
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error logging in client SSO: ', error);
        return {
            error: true,
            detail:
                error?.response?.data?.detail ||
                'An error occurred while attempting the SSO.',
            status: error?.response?.status || 500,
        };
    }
};

// Lookup operations
export const fetchSSOInitiationTypes = async () => {
    try {
        const response = await systemHttp.get('/v2/system/sso-initiation-type');
        return response.data;
    } catch (error) {
        console.error('Error fetching SSO initiation types:', error);
        throw error;
    }
};

export const fetchTenantSSOStatuses = async () => {
    try {
        const response = await systemHttp.get('/v2/system/tenant-sso-status');
        return response.data;
    } catch (error) {
        console.error('Error fetching tenant SSO statuses:', error);
        throw error;
    }
};

export const fetchSSOVendorEnvironments = async () => {
    try {
        const response = await systemHttp.get(
            '/v2/system/sso-vendor-environment',
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching SSO vendor environments:', error);
        throw error;
    }
};

export const fetchSSOAttributeValues = async () => {
    try {
        const tenantId = getTenantId();
        const response = await systemHttp.get(
            `/v2/system/sso-attribute-value${tenantId === 0 ? '' : `?tenantId=${tenantId}`}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching SSO attribute values:', error);
        throw error;
    }
};

export const fetchEncryptionCertificates = async () => {
    try {
        const response = await systemHttp.get(
            '/v2/system/certificate?certificatePurposeIds=1',
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching encryption certificates:', error);
        throw error;
    }
};

export const createSystemCertificate = async certificateData => {
    const formData = new FormData();
    formData.append('Certificate', certificateData.certificate);
    formData.append('Name', certificateData.name);
    formData.append('CertificatePurposeId', certificateData.purposeId);
    formData.append('Id', 0);
    const response = await systemHttp.post(`/v2/system/certificate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (response.status === 200) {
        return response.data;
    } else {
        throw new Error('Error uploading file');
    }
};

export const fetchCertificateById = async id => {
    try {
        const response = await systemHttp.get(`/v2/system/certificate/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching certificate with ID ${id}:`, error);
        throw error;
    }
};

export const fetchSigningCertificates = async () => {
    try {
        const response = await systemHttp.get(
            '/v2/system/certificate?certificatePurposeIds=2',
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching signing certificates:', error);
        throw error;
    }
};
